import {
    WorkflowStepState,
    CircleCircle,
    WorkflowStepDefinition,
} from '@calm/evexia-web-sdk';
import { useUpdateWorkflowStep } from '@rhg/hooks/use-update-workflow-step';
import { logger } from '@rhg/libs/logger';
import { useMutation } from '@rhg/libs/react-query';
import { FtueStoredStepName, StepStates } from '@rhg/utils/workflows';
import { getCachedVideoId, storeCurrentStep } from '../../utils';

export interface UpdateFtueWorkflowStepParams {
    workflowDefnId: string;
    stepDefnId: string;
    isSkipped?: boolean;
    activeCircle?: CircleCircle;
    isFtueWorkflowLoading: boolean;
    isFtueWorkflowEnabled: boolean;
    userSub?: string;
    onStepUpdate: (step: WorkflowStepDefinition | undefined) => void;
    skipCachedVideoStepForward: (
        stepDefnId: string,
        workflowDefnId: string
    ) => Promise<void>;
    restartWorkflow: (workflowDefnId: string) => Promise<string | undefined>;
}

// Mutation hook for updating FTUE workflow step
export function useUpdateFtueWorkflowStep() {
    const { updateWorkflowStepAsync } = useUpdateWorkflowStep();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (params: UpdateFtueWorkflowStepParams) => {
            const {
                workflowDefnId,
                stepDefnId,
                isSkipped,
                activeCircle,
                isFtueWorkflowLoading,
                isFtueWorkflowEnabled,
                userSub,
                onStepUpdate,
                skipCachedVideoStepForward,
                restartWorkflow,
            } = params;

            if (
                !!activeCircle &&
                !isFtueWorkflowLoading &&
                isFtueWorkflowEnabled
            ) {
                const stepState = isSkipped
                    ? StepStates.SKIPPED
                    : StepStates.COMPLETED;
                try {
                    const response = await updateWorkflowStepAsync({
                        workflowDefnId,
                        stepDefnId,
                        request: {
                            state: stepState as WorkflowStepState,
                        },
                    });
                    const stepObj = response.data?.stepDefn;
                    const newStep = stepObj?.defnId;
                    const hasCachedVideo = newStep
                        ? !!(await getCachedVideoId(newStep, userSub))
                        : false;

                    if (newStep && hasCachedVideo) {
                        await skipCachedVideoStepForward(
                            newStep,
                            workflowDefnId
                        );
                    } else {
                        onStepUpdate(stepObj);
                        if (newStep) {
                            storeCurrentStep(FtueStoredStepName, newStep);
                        }
                    }

                    return response;
                } catch (error: unknown) {
                    const errorCode =
                        typeof error === 'object' &&
                        error !== null &&
                        'response' in error &&
                        typeof error.response === 'object' &&
                        error.response !== null &&
                        'data' in error.response &&
                        typeof error.response.data === 'object' &&
                        error.response.data !== null &&
                        'errorCode' in error.response.data
                            ? error.response.data.errorCode
                            : undefined;
                    if (errorCode === 'step_defn_id_not_found_in_db') {
                        await restartWorkflow(workflowDefnId);
                    }

                    logger.error(
                        'Error updating next workflow step:',
                        {
                            stepDefnId,
                            stepState,
                            workflowDefnId,
                        },
                        error
                    );
                    throw error;
                }
            }
        },
    });

    return {
        updateFtueWorkflowStep: mutate,
        updateFtueWorkflowStepAsync: mutateAsync,
        ...otherProps,
    };
}
